import { forwardRef, useImperativeHandle, useRef } from "react";
import {
  hasDisplayContent,
  resolveDisplayConfig,
} from "../../../../lib/forms/displayField";
import { DiskImage } from "../../../media/DiskImage";
import { sanitizeHtml } from "../../../../lib/media/sanitizeHtml";
import { FieldDescriptionText } from "../../field-ui";
import type { FieldDto } from "../../../../types/field";
import type { FormFieldInputProps, FormFieldRef } from "../../types";

interface DisplayInputProps extends FormFieldInputProps {
  field: FieldDto;
  label?: string;
  labelMuted?: boolean;
}

const DisplayInput = forwardRef<FormFieldRef, DisplayInputProps>(
  (props, ref) => {
    const shellRef = useRef<HTMLDivElement>(null);
    const config = resolveDisplayConfig(props.field);
    const hasContent = hasDisplayContent(config);

    useImperativeHandle(ref, () => ({
      setValidationState: () => {},
      value: "",
      fieldId: props.fieldId,
      fieldName: props.fieldName,
      scrollToElement: (options) => {
        shellRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          ...options,
        });
      },
      validate: () => true,
      clear: () => {},
    }));

    if (!hasContent) {
      return null;
    }

    const labelText = (props.label ?? props.field.label ?? "").trim();

    return (
      <div
        ref={shellRef}
        className="form-display mt-2 w-full scroll-mt-24 sm:scroll-mt-8"
      >
        {labelText ? (
          <div className="mb-2">
            <div
              className={[
                "form-display__label font-onest text-sm font-semibold",
                props.labelMuted ? "text-slate-400" : "text-ink",
              ].join(" ")}
            >
              {labelText}
            </div>
            <FieldDescriptionText className="mt-0.5" />
          </div>
        ) : (
          <FieldDescriptionText className="mb-2" />
        )}
        {config.image_url ? (
          <DiskImage
            src={config.image_url}
            alt=""
            className="form-display__image mb-3 max-w-full rounded-lg"
            loading="lazy"
          />
        ) : null}
        {config.content ? (
          <div
            className="form-display__content font-onest text-sm leading-relaxed text-ink"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(config.content) }}
          />
        ) : null}
      </div>
    );
  },
);

DisplayInput.displayName = "DisplayInput";

export { DisplayInput };
export type { DisplayInputProps };
